import { defineStore } from "pinia";
import api from "@/modules/api";
import { ApiGetImagePrompt } from "@/apis/prompt";
import { StorageGet, StorageSet } from "@/tools/localStorage";
import i18n from "@/locales";
import axios from "axios";
import { useUserPromptUrl } from "@/use/common";
import {
  getClothingNsfwTag,
  getOtherNsfwTag,
  getPoseNsfwTag,
  getPosesTag,
} from "@/tools/nsfwPrompt";
import { concatPromptString } from "@/tools/obj";
import { sessionStorageSet } from "@/tools/sessionStorage";

const PER_PAGE = 1222;

export interface ImagePromptItem {
  id: number;
  text: string;
  displayText: string;
  weight?: string;
  parentId: number;
  children: ImagePromptItem[];
  path: string;
  sort?: number;
  label?: string;
}

export const CHARACTER_CATE_ID = 1;
export const ENV_CATE_ID = 984;
export const FACE_EXPRESSION_CATE_ID = 826;
export const POSE_CATE_ID = 58;
export const CLOTHING_CATE_ID = 237;
export const CAMERA_CATE_ID = 1175;
export const STYLE_CATE_ID = 50;
export const NSFW_CATE_OTHER_ID = -1;
export const FACE_CATE_IDs = [
  826, // 表情
  684, // 眼睛
  679, // 眉毛
  724, // 嘴巴
  51, // 艺术形式
  52, // 动漫游戏
  53, // 光影
];

export const NOBODY_IDs = [
  984, // 环境
  1175, // 视角
];

export const SINGLE_IDs = [
  826, // 表情
  58, // 姿势
  237, // 服装
];

export const CHARACTER_NEW_IDs = [...SINGLE_IDs, ...NOBODY_IDs.reverse()];

const handlePromptText = (text: string, d: string): string => {
  if (text.includes(",")) return "";
  for (
    ;
    text.startsWith("((") && text.endsWith("))");
    text = text.slice(1, -1)
  );
  return text;
};

export const usePrompts = defineStore("prompts", {
  state: () => {
    return {
      imgPrompts: [] as ImagePrompt[],
      maxId: 0,
    };
  },
  getters: {
    promptId2Item: (state): Record<number, ImagePromptItem> => {
      if (state.imgPrompts.length === 0) return {};
      const map = {} as Record<number, ImagePromptItem>;
      // 根据rootId分组
      const rootIdSet = new Set<number>();
      const textSet = new Set<string>();
      state.imgPrompts.forEach((prompt) => {
        let { id, text, parentId, displayText, sort } = prompt;
        text = text.trim();
        if (text) {
          if (text.includes(":")) return;
          if (textSet.has(text)) return;
          text = handlePromptText(text, displayText);
          if (!text) return;
          textSet.add(text);
        }

        map[id!] = {
          id: id!,
          text,
          displayText,
          parentId,
          path: "",
          children: [],
          sort,
        };
        state.maxId = Math.max(state.maxId, id!);
        if (!parentId) rootIdSet.add(id!);
      });
      [
        ...getClothingNsfwTag().children,
        ...getPoseNsfwTag().children,
        ...getOtherNsfwTag().children,
        ...getPosesTag().children,
      ].forEach((item) => {
        const { id, text, displayText, parentId } = item;
        if (textSet.has(text)) return;
        textSet.add(text);
        map[id] = {
          id,
          text,
          displayText,
          parentId,
          path: "",
          children: [],
          sort: -1,
        };
      });

      // 遍历第二遍，将children加入（避免第一遍时parentId还没遍历到）
      state.imgPrompts.forEach((prompt) => {
        const { id, parentId } = prompt;
        if (parentId !== null && map[parentId] && map[id!]) {
          map[parentId].children.push(map[id!]);
        }
      });
      // 遍历第三遍，构造parentIdPath
      const dfs = (item: ImagePromptItem, path: string) => {
        if (item.children.length > 0) {
          item.children.sort((a, b) => a.sort! - b.sort!);
          item.children.forEach((child) => {
            child.path = path
              ? `${path}-${child.parentId}`
              : `${child.parentId}`;
            dfs(child, child.path);
          });
        }
      };
      Array.from(rootIdSet).forEach((id) => {
        const target = map[id];
        dfs(target, target.path);
      });

      return map;
    },
    envCatePrompts(): ImagePromptItem[] {
      return this.promptId2Item[ENV_CATE_ID]?.children ?? [];
    },
    faceExpressionPrompts(): ImagePromptItem[] {
      return this.promptId2Item[FACE_EXPRESSION_CATE_ID]?.children ?? [];
    },
    posePrompts(): ImagePromptItem[] {
      return this.promptId2Item[POSE_CATE_ID]?.children ?? [];
    },
    clothingPrompts(): ImagePromptItem[] {
      return this.promptId2Item[CLOTHING_CATE_ID]?.children ?? [];
    },
    cameraCatePrompts(): ImagePromptItem[] {
      return this.promptId2Item[CAMERA_CATE_ID]?.children ?? [];
    },
    stylePrompts(): ImagePromptItem[] {
      return this.promptId2Item[STYLE_CATE_ID]?.children ?? [];
    },
    facePrompts(): ImagePromptItem[] {
      let list: ImagePromptItem[] = [];
      FACE_CATE_IDs.forEach((id) => {
        if (this.promptId2Item[id]?.children) list.push(this.promptId2Item[id]);
      });
      return list;
    },
    characterCatePrompts(): ImagePromptItem[] {
      return this.promptId2Item[CHARACTER_CATE_ID]?.children ?? [];
    },
    singleCharacterPrompts(): ImagePromptItem[] {
      let list: ImagePromptItem[] = [];
      CHARACTER_NEW_IDs.forEach((id) => {
        if (this.promptId2Item[id]?.children) list.push(this.promptId2Item[id]);
      });
      return list;
    },
    singlePrompts(): ImagePromptItem[] {
      let list: ImagePromptItem[] = [];
      SINGLE_IDs.forEach((id) => {
        if (this.promptId2Item[id]?.children) list.push(this.promptId2Item[id]);
      });
      return list;
    },
    noCharacterPrompts(): ImagePromptItem[] {
      let list: ImagePromptItem[] = [];
      NOBODY_IDs.forEach((id) => {
        if (this.promptId2Item[id]?.children) list.push(this.promptId2Item[id]);
      });
      return list;
    },
    text2PromptMap() {
      const map: Map<string, ImagePromptItem> = new Map();
      Object.values(this.promptId2Item).forEach((prompt) =>
        map.set(prompt.text, prompt)
      );
      // getPoseNsfwTag().children.forEach((item) => {
      //   map.set(item.text, item);
      // });
      // getClothingNsfwTag().children.forEach((item) => {
      //   map.set(item.text, item);
      // });
      // getOtherNsfwTag().children.forEach((item) => {
      //   map.set(item.text, item);
      // });
      return map;
    },
  },
  actions: {
    async fetchImgPrompts() {
      const res = await api.readImagePromptPage({
        pagination: { page: 1, perPage: PER_PAGE },
      });
      return res.page.data;
    },
    async getOrFetchImgPrompts(isForce?: boolean) {
      if (!isForce && this.imgPrompts.length > 0) return this.imgPrompts;
      let cacheResp = StorageGet("imgPrompts");
      if (!cacheResp) cacheResp = await this._fetchImgPrompts();
      else {
        let res = await ApiGetImagePrompt(true);
        if (cacheResp.dtUpdate !== res.item.dtUpdate)
          cacheResp = await this._fetchImgPrompts();
      }
      const isChinese = i18n.global.locale.value === "zh-CN";
      if (!cacheResp.en || !cacheResp.zh) {
        cacheResp = await this._fetchImgPrompts();
      }
      this.imgPrompts = isChinese ? cacheResp.zh : cacheResp.en;
      return this.imgPrompts;
    },
    async _fetchImgPrompts() {
      const res = await ApiGetImagePrompt();
      const { id, zh, en, dtUpdate } = res.item;
      if (!zh && !en) {
        const { data } = await axios.get(
          useUserPromptUrl(id) + "?t=" + new Date(dtUpdate).getTime()
        );
        res.item = { ...res.item, ...data };
      }
      sessionStorageSet("imgPrompts", res.item);
      return res.item;
    },
    text2PromptTextList(text: string): string[] {
      return text
        .replace(/\s*,\s*/g, ",")
        .split(",")
        .filter((item) => item);
    },
    /** 返回用户手动填写的提示词 */
    getCustomPrompt(
      originalText: string,
      promptList: Omit<ImagePromptItem, "children">[]
    ): string {
      const textList = this.text2PromptTextList(originalText);
      const promptTextList = this.getTextPrompt(
        promptList as ImagePromptItem[]
      ).split(",");
      const customPromptList = textList.filter(
        (text) => !promptTextList.includes(text.trim())
      );
      return customPromptList.join(",");
    },
    // 不带 trait 的 prompt
    getPromptListByTextImage(entity: TextImage) {
      let { prompt } = entity;
      if (!prompt) prompt = "";
      prompt = prompt.trim();
      if (prompt.length > 0 && prompt[prompt.length - 1] !== ",") prompt += ",";
      prompt += this.getTextByTextImage(entity).positive || "";
      return this.getPromptListByText(prompt);
    },
    // 带 trait 的 prompt
    getAllPromptListByTextImage(entity: TextImage) {
      let { prompt, mateTrait } = entity;
      if (!prompt) prompt = "";
      prompt = prompt.trim();
      if (prompt.length > 0 && prompt[prompt.length - 1] !== ",") prompt += ",";
      prompt += mateTrait?.prompt || "";
      if (prompt.length > 0 && prompt[prompt.length - 1] !== ",") prompt += ",";
      prompt += this.getTextByTextImage(entity).positive || "";
      return this.getPromptListByText(prompt);
    },
    getTextByTextImage(entity: TextImage) {
      const {
        promptJson,
        matePrompt,
        mateNegativePrompt,
        prompt,
        negativePrompt,
      } = entity;
      if (!promptJson) return { positive: "", negative: "" };
      const { env, clothing, faceExpression, pose } = promptJson;
      if (!clothing || !faceExpression || !pose || !env)
        return { positive: "", negative: "" };
      let positive = concatPromptString([
        prompt,
        matePrompt || "",
        clothing.prompt,
        faceExpression.prompt,
        pose.prompt,
      ]);
      let negative = concatPromptString([
        negativePrompt,
        mateNegativePrompt || "",
        clothing.negativePrompt,
        faceExpression.negativePrompt,
        pose.negativePrompt,
      ]);

      positive = concatPromptString([positive, env.prompt]);
      negative = concatPromptString([negative, env.negativePrompt]);

      return { positive, negative };
    },
    getPromptListByText(text: string): ImagePromptItem[] {
      if (text === "") return [];
      const textList = this.text2PromptTextList(text);
      return textList
        .map((_text) => {
          let [text, weight] = _text.split(":");
          text = text.trim().replace(/\(|\)/g, ""); // 去除括号
          const targetPrompt = this.text2PromptMap.get(text);
          if (!targetPrompt) return null;
          if (weight) targetPrompt.weight = weight.trim();
          return targetPrompt;
        })
        .filter((prompt) => prompt) as ImagePromptItem[];
    },
    getTextPrompt(promptItems: ImagePromptItem[]) {
      return promptItems
        .map((item) => {
          if (item.weight) {
            return `(${item.text}:${item.weight})`;
          } else {
            return item.text;
          }
        })
        .join(",");
    },
    randomPrompts(prompts: ImagePromptItem[], categoryCount: number) {
      const promptList: ImagePromptItem[] = [];
      if (prompts.length === 0) {
        return promptList;
      }
      const validCandidate = this.getLeafNodes(prompts);
      const allPossibleIdx = new Set(
        Object.keys(validCandidate).map((item) => Number(item))
      );
      for (let i = 0; i < categoryCount; i++) {
        const idx = Array.from(allPossibleIdx).splice(
          Math.floor(Math.random() * allPossibleIdx.size),
          1
        )[0];
        allPossibleIdx.delete(idx);
        promptList.push(validCandidate[idx]);
      }
      return promptList;
    },
    getLeafNodes(promptItems: ImagePromptItem[]) {
      // 递归
      const leafNodes: ImagePromptItem[] = [];
      promptItems.forEach((item) => {
        if (item.children.length === 0) {
          leafNodes.push(item);
        } else {
          leafNodes.push(...this.getLeafNodes(item.children));
        }
      });
      return leafNodes;
    },
    randomCharacterPrompt() {
      // 选出5个分类，每个分类中选一个
      return this.randomPrompts(this.characterCatePrompts, 5);
    },
    randomCameraPrompt() {
      const categoryList = this.cameraCatePrompts;
      const promptList: ImagePromptItem[] = [];
      if (categoryList.length === 0) {
        return promptList;
      }
      promptList.push(
        categoryList[0].children[
          Math.floor(Math.random() * categoryList[0].children.length)
        ]
      );
      return promptList;
    },
    randomFaceExpressionPrompt() {
      return this.randomPrompts(this.faceExpressionPrompts, 1);
    },
    randomPosePrompt() {
      return this.randomPrompts(this.posePrompts, 3);
    },
    randomClothingPrompt() {
      return this.randomPrompts(this.clothingPrompts, 3);
    },
    randomEnvPrompt() {
      // 选出三个分类，每个分类中选一个
      return this.randomPrompts(this.envCatePrompts, 3);
    },
    randomStylePrompt() {
      return this.randomPrompts(this.stylePrompts, 1);
    },
    randomFacePrompt() {
      return this.randomPrompts(this.facePrompts, 3);
    },
    isCharacterPrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text &&
        prompt.path.split("-").includes(CHARACTER_CATE_ID.toString())
      );
    },
    isEnvPrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text && prompt.path.split("-").includes(ENV_CATE_ID.toString())
      );
    },
    isFaceExpressionPrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text &&
        prompt.path.split("-").includes(FACE_EXPRESSION_CATE_ID.toString())
      );
    },
    isPosePrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text &&
        prompt.path.split("-").includes(POSE_CATE_ID.toString())
      );
    },
    isClothingPrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text &&
        prompt.path.split("-").includes(CLOTHING_CATE_ID.toString())
      );
    },
    isCameraPrompt(prompt: Omit<ImagePromptItem, "children">): boolean {
      return (
        !!prompt.text &&
        prompt.path.split("-").includes(CAMERA_CATE_ID.toString())
      );
    },
  },
});
